import { APP_NAME } from "./app.constants";
export interface BlogArticle {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  accent: string;
}

export const BLOG_ARTICLES: readonly BlogArticle[] = [
  {
    slug: "ometv-alternatives",
    category: "Chat platforms",
    title: "OmeTV alternatives: what to look for in a random chat site",
    excerpt:
      "A practical guide to comparing privacy, moderation, conversation quality, and the features that matter when meeting strangers online.",
    date: "March 13, 2026",
    readTime: "7 min read",
    accent: "sunset",
  },
  {
    slug: "best-websites-to-chat-with-strangers",
    category: "Chat platforms",
    title: "Best websites to chat with strangers",
    excerpt:
      "How to choose a random chat platform that respects your privacy and helps conversations feel natural rather than noisy.",
    date: "March 13, 2026",
    readTime: "8 min read",
    accent: "night",
  },
  {
    slug: "how-to-talk-to-a-stranger-online",
    category: "Social skills",
    title: "How to talk to a stranger online: examples that help",
    excerpt:
      "Conversation starters, boundaries, and small habits that make an online hello feel more comfortable for both people.",
    date: "March 9, 2026",
    readTime: "6 min read",
    accent: "sky",
  },
];

export interface AlternativePageContent {
  slug: string;
  brand: string;
  title: string;
  description: string;
  intro: string;
  angle: string;
  strengths: string[];
  strangeryapFit: string;
  faq: { question: string; answer: string }[];
  accent: string;
}

export const ALTERNATIVE_PAGES: Readonly<
  Record<string, AlternativePageContent>
> = {
  "omegle-alternative": {
    slug: "omegle-alternative",
    brand: "Omegle",
    title: "A thoughtful Omegle alternative for anonymous text chat",
    description: `Looking for an Omegle alternative? ${APP_NAME} offers free anonymous text chat, interest-based matching, and simple controls for meeting people online.`,
    intro: `Omegle introduced millions of people to spontaneous one-on-one conversations with strangers. ${APP_NAME} takes the core idea — a random match, a blank page, and a chance at a real exchange — and rebuilds it with more emphasis on privacy, common ground, and practical safety tools. No account, no public profile, just a conversation.`,
    angle: "For people who want a calmer, text-first starting point",
    strengths: [
      "Anonymous guest access — no email or account required",
      "Interest-aware matching with fallback to unmatched users",
      "Skip, block, and report controls in every chat",
      "No public profile or stored message history",
    ],
    strangeryapFit: `${APP_NAME} is currently focused on text chat — the format that lets you connect without camera pressure or the need to share more than you are comfortable with. It is a practical Omegle alternative for anyone who wants a clean, private, conversation-first experience from a browser.`,
    faq: [
      {
        question: `Is ${APP_NAME} a free Omegle alternative?`,
        answer: `Yes. ${APP_NAME} currently offers free anonymous text chat without requiring an account to begin. There is no subscription or payment needed to start a conversation.`,
      },
      {
        question: "Can I talk to strangers without a camera?",
        answer: `Yes. ${APP_NAME} is text-first, so a camera is never required. The entire chat experience works through text, which means you decide how much of yourself to share.`,
      },
      {
        question: "How does interest matching work?",
        answer: `When you start a chat you can add interests such as music, gaming, travel, or any topic you enjoy. ${APP_NAME} prioritises matching you with someone who shares at least one of those interests, but will still find a match when there is no overlap so you are never left waiting indefinitely.`,
      },
      {
        question: "Is my conversation private?",
        answer: `Messages are relayed in real time and not stored on ${APP_NAME} servers after the session ends. Your chat history is saved only in your own browser and is never tied to an account or identity.`,
      },
      {
        question: "What happens if I want to end a conversation?",
        answer:
          "You can skip to the next person at any time using the Skip button in the chat. If someone behaved badly you can also block them, which prevents them from appearing in future matches during your current session.",
      },
    ],
    accent: "violet",
  },
  "ometv-alternative": {
    slug: "ometv-alternative",
    brand: "OmeTV",
    title: "An OmeTV alternative focused on comfortable conversations",
    description:
      "Explore a text-first OmeTV alternative for meeting strangers online without starting on camera or creating a public profile.",
    intro: `OmeTV built its audience around fast video matching, but video chat is not comfortable for everyone. Some people prefer to feel out a conversation in writing before deciding whether to share more. ${APP_NAME} is an anonymous text chat platform that prioritises that quieter kind of connection — interest-based matching, no camera required, and a single button to move on when you are ready.`,
    angle: "For people who prefer privacy before presence",
    strengths: [
      "No camera required — text chat only",
      "Quick random matching with interest-aware priority",
      "Shared interests displayed at the start of each conversation",
      "Block and report controls available throughout every chat",
    ],
    strangeryapFit: `If you are looking at OmeTV alternatives because you find video pressure uncomfortable, ${APP_NAME} is worth trying. It is built around text so the first message carries the conversation, not your appearance. Start with a nickname, pick a few interests, and meet someone new in seconds — no camera, no account, no setup.`,
    faq: [
      {
        question: `Does ${APP_NAME} require video chat?`,
        answer: `No. The current ${APP_NAME} experience is entirely anonymous text chat. You can join, match, and have a full conversation without turning on a camera or microphone.`,
      },
      {
        question: "Can I skip a match like on OmeTV?",
        answer: `Yes. The Skip button is available throughout every chat. When you skip, ${APP_NAME} immediately searches for your next match using your current interest list.`,
      },
      {
        question: "How long does it take to find a match?",
        answer: `Most matches are found within a few seconds. Shared interests are prioritised, but ${APP_NAME} will still connect you with someone even if no shared interest is found.`,
      },
      {
        question: "Do I need to create an account?",
        answer:
          "No. All you need is a display name and optionally a few interests. There is no email, password, or profile required to start chatting.",
      },
    ],
    accent: "coral",
  },
  "chatroulette-alternative": {
    slug: "chatroulette-alternative",
    brand: "Chatroulette",
    title: "A simple Chatroulette alternative for text conversations",
    description:
      "Meet new people through a lightweight anonymous text-chat experience with interests, skip controls, and privacy-conscious matching.",
    intro: `Chatroulette popularised the idea of a random one-on-one video match with a complete stranger. ${APP_NAME} keeps the randomness and the excitement of not knowing who you will meet next, but replaces the webcam-first format with text. The result is a lighter, lower-pressure experience where the conversation does the work — and you can skip to someone new in one tap whenever the mood shifts.`,
    angle: "For people who want randomness without the webcam-first experience",
    strengths: [
      "Fast anonymous text matching — no video required",
      "Global conversations across time zones",
      "Optional interests to improve match quality",
      "One-tap skip, block, and report from inside every chat",
    ],
    strangeryapFit: `${APP_NAME} is a text-chat Chatroulette alternative for people who want the surprise of a random connection without a camera pointed at them. Pick a display name, choose a few interests if you like, and start talking. The interface is clean, the matching is fast, and the controls are always within reach.`,
    faq: [
      {
        question: "Is this a video Chatroulette alternative?",
        answer: `Not currently. ${APP_NAME} provides anonymous text chat today. It is the right choice if you are looking for the random-matching concept of Chatroulette but prefer to start with words rather than a video feed.`,
      },
      {
        question: "Can I match by interests like on other platforms?",
        answer: `Yes. You can add interests before your search, and ${APP_NAME} will prioritise people who share them. If no match with shared interests is available, you will still be connected so you are never stuck waiting.`,
      },
      {
        question: `Is ${APP_NAME} completely anonymous?`,
        answer:
          "Yes. No account, email, or real name is required. You choose a display name for the session, and your personal details stay private throughout.",
      },
      {
        question: "How do I end a conversation I am not enjoying?",
        answer:
          "Use the Skip button to move on and find a new match instantly. If someone behaves badly, use Block to prevent them from appearing in future matches during your session.",
      },
    ],
    accent: "blue",
  },
  "emerald-chat-alternative": {
    slug: "emerald-chat-alternative",
    brand: "Emerald Chat",
    title: "An Emerald Chat alternative built around simple text matching",
    description:
      "Try a focused Emerald Chat alternative with anonymous text chat, interest-aware matching, and practical session safety controls.",
    intro: `Emerald Chat brought interest-based matching to random chat, which made first messages easier and conversations more likely to go somewhere. ${APP_NAME} shares that philosophy but keeps the experience simpler: no karma score, no account system, and no public profile to build and maintain. Just a nickname, a handful of interests, and a match in seconds.`,
    angle: "For people who want common ground without complicated profiles",
    strengths: [
      "Interest-aware matching — conversations start with something in common",
      "Fallback matching when no interest overlap is found",
      "Session-scoped exclusions so blocked users stay out of your matches",
      "Guest-first text chat — no account or karma system",
    ],
    strangeryapFit: `${APP_NAME} is a clean Emerald Chat alternative for anyone who liked the interest-matching idea but found account-based platforms too involved. There is nothing to earn, rank, or maintain. Every session is fresh, private, and focused on the conversation in front of you.`,
    faq: [
      {
        question: `Does ${APP_NAME} match people by interests?`,
        answer: `Yes. Shared interests improve the quality of your match. ${APP_NAME} will prioritise people who share your listed interests but will still find a match when there is no overlap, so you are never left without a conversation.`,
      },
      {
        question: "Can I block someone I do not want to see again?",
        answer:
          "Yes. Blocking excludes that person from matching with you for the rest of your current session. The block is applied immediately and does not require a report to take effect.",
      },
      {
        question: "Is there a karma or rating system like Emerald Chat?",
        answer: `No. ${APP_NAME} does not use karma, ratings, or reputation scores. Every user starts on equal footing and your access to matching is never gated by your behaviour score.`,
      },
      {
        question: `How private is ${APP_NAME} compared to Emerald Chat?`,
        answer: `${APP_NAME} requires no account, email, or personal details. You choose a display name for the session and nothing else is recorded or tied to your identity. Messages are not stored on ${APP_NAME} servers after the session ends.`,
      },
    ],
    accent: "green",
  },
  "monkey-app-alternative": {
    slug: "monkey-app-alternative",
    brand: "Monkey App",
    title: "A Monkey App alternative without a public social feed",
    description:
      "Meet someone new in a private, anonymous text conversation instead of joining another public social feed or profile-driven app.",
    intro: `Monkey App and platforms like it mix random matching with social features — followers, public profiles, and feeds. That works for some people, but others find it easier to connect when it is just two of you in a private text conversation. ${APP_NAME} keeps it one-to-one. No audience, no performance, no social obligation to stay or follow up.`,
    angle: "For people who want a private first hello",
    strengths: [
      "One-to-one anonymous text chat — no public feed or followers",
      "No social login, download, or account required",
      "Interest tags give conversations a natural starting point",
      "Leave or skip at any time with no social consequence",
    ],
    strangeryapFit: `${APP_NAME} runs in a browser, so there is no app to download and no notification permissions to grant. It is a practical Monkey App alternative for anyone who wants the energy of meeting someone new without committing to a social platform. Start a conversation, see where it goes, and move on when you are ready.`,
    faq: [
      {
        question: `Is ${APP_NAME} a social media app?`,
        answer: `No. ${APP_NAME} is built around one-to-one anonymous conversations, not public profiles or social feeds. There are no followers, likes, or posts. Every session is private between the two matched users.`,
      },
      {
        question: "Do I need to download an app?",
        answer: `No. ${APP_NAME} runs entirely in your web browser on desktop and mobile. There is no app to install and no permissions required beyond what the browser already supports.`,
      },
      {
        question: `Is there a minimum age for ${APP_NAME}?`,
        answer: `Yes. ${APP_NAME} is for users aged 18 and over. By starting a session, you confirm that you meet the age requirement.`,
      },
      {
        question: `What information does ${APP_NAME} store about me?`,
        answer: `${APP_NAME} does not collect personal information. You provide a display name for the session and optionally some interests. No email, phone number, or identity verification is required. Messages are not stored on ${APP_NAME} servers after the session ends.`,
      },
    ],
    accent: "orange",
  },
};
